import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, DollarSign, FileText, TrendingUp, ArrowRight, Moon, Sun, Users, CheckCircle2, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/hooks/useTheme';
import { loadFeatureFlags } from '@/components/utils/featureFlags';

export default function Why() {
  const { isDark, toggleTheme } = useTheme();
  const [featureFlags, setFeatureFlags] = useState({
    allow_company_registration: true
  });

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      const flags = await loadFeatureFlags();
      setFeatureFlags(flags);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      <header className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">BenefitsPro</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" onClick={() => (window.location.href = '/')}>
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 text-sm px-4 py-2">
            <Shield className="w-4 h-4 mr-2" />
            For Companies
          </Badge>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
            Why BenefitsPro
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            A modern Section 125 benefits experience that reduces admin work, improves employee adoption,
            and keeps enrollment organized.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            {featureFlags.allow_company_registration && (
              <Button
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-lg px-8 py-6"
                onClick={() => (window.location.href = '/CompanySignup')}
              >
                Start company setup
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
            <Button size="lg" variant="outline" className="text-lg px-8 py-6" onClick={() => api.auth.redirectToLogin()}>
              Sign in
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-3 gap-6"
        >
          <Card className="h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Drive participation</h3>
              <p className="text-muted-foreground">
                A clear employee portal helps people understand options and complete enrollment.
              </p>
            </CardContent>
          </Card>

          <Card className="h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Stay organized</h3>
              <p className="text-muted-foreground">
                Enrollment steps, invitations, and status tracking keep the process predictable.
              </p>
            </CardContent>
          </Card>

          <Card className="h-full">
            <CardContent className="p-6">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Reduce admin load</h3>
              <p className="text-muted-foreground">
                Self-serve enrollment and a single dashboard help HR teams spend less time chasing paperwork.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="mt-10">
            <CardContent className="p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Who is this for?</h2>
                <p className="text-muted-foreground">
                  Companies and HR teams running Section 125 benefits — plus employees who want a simpler enrollment experience.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <Button variant="outline" className="w-full md:w-auto" onClick={() => (window.location.href = '/hr')}>
                  HR overview
                </Button>
                <Button variant="outline" className="w-full md:w-auto" onClick={() => (window.location.href = '/me')}>
                  Employee overview
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* The problem */}
        <section className="mt-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">Why companies choose BenefitsPro</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                Enrollment is high-stakes and time-bound. BenefitsPro helps make it structured, trackable, and easier for employees to complete.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Better employee experience</h3>
                  <p className="text-muted-foreground">
                    A clean portal encourages completion by making choices and steps easy to understand.
                  </p>
                </CardContent>
              </Card>

              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">More predictable operations</h3>
                  <p className="text-muted-foreground">
                    Clear status and workflows reduce last-minute surprises and manual follow-up.
                  </p>
                </CardContent>
              </Card>

              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Centralized enrollment record</h3>
                  <p className="text-muted-foreground">
                    Keep enrollment information and progress in one place instead of across emails and spreadsheets.
                  </p>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </section>

        {/* What you get */}
        <section className="mt-16 bg-muted/30 rounded-2xl p-8 md:p-12">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">What you get</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                The core outcomes most teams want from enrollment tools.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="h-full">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3">For HR teams</h3>
                  <div className="space-y-3 text-muted-foreground">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>Guided company onboarding flow.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>Invitations tied to your organization.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>Enrollment progress visibility for follow-ups.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="h-full">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3">For employees</h3>
                  <div className="space-y-3 text-muted-foreground">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>A clear portal for reviewing benefits.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>A guided enrollment experience from start to finish.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <p>Invitation-code access to the correct company program.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">Company FAQ</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                Answers to the most common rollout questions.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  q: 'Is this for employees only or for HR too?',
                  a: 'Both. Employees enroll in the portal, and HR teams manage setup and status.'
                },
                {
                  q: 'How do employees join our program?',
                  a: 'They sign in and enter your organization’s invitation code.'
                },
                {
                  q: 'Can we start small?',
                  a: 'Yes. You can begin onboarding and invite employees when you’re ready.'
                },
                {
                  q: 'Where should we begin?',
                  a: 'Start with company signup, then proceed through onboarding.'
                }
              ].map((item) => (
                <Card key={item.q} className="h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="font-semibold mb-2">{item.q}</p>
                        <p className="text-muted-foreground">{item.a}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Final CTA */}
        <section className="mt-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-500 border-0">
              <CardContent className="p-10 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Start company setup</h2>
                <p className="text-white/90 text-lg mb-6">
                  Create your company account and begin onboarding.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  {featureFlags.allow_company_registration && (
                    <Button
                      variant="secondary"
                      onClick={() => (window.location.href = '/CompanySignup')}
                    >
                      Get started
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                  <Button variant="secondary" onClick={() => (window.location.href = '/hr')}>
                    HR overview
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>
      </main>

      <footer className="bg-muted border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-muted-foreground">© 2025 BenefitsPro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
